import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { ThemeToggle } from "../ThemeToggle";

// SVG icon definitions
const I = {
  hamburger: '<path d="M3 6h18M3 12h18M3 18h18"/>',
  search: '<circle cx="11" cy="11" r="7"/><path d="m21 21-4-4"/>',
  chevR: '<path d="m9 6 6 6-6 6"/>',
  bell: '<path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z"/><path d="M10.5 20a2 2 0 0 0 3 0"/>',
  user: '<circle cx="12" cy="8" r="3.5"/><path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6"/>',
  card: '<rect x="2.5" y="5" width="19" height="14" rx="2.5"/><path d="M2.5 9.5h19"/><path d="M6 14.5h4"/>',
  chev: '<path d="m6 9 6 6 6-6"/>',
};

function svg(p, w) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={w || 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: p }}
    />
  );
}

// Map routes to breadcrumb labels
const BREADCRUMB_MAP = {
  "/dashboard": { label: "Dashboard", crumbs: [{ label: "Home", href: "/dashboard" }, { label: "Dashboard" }] },
  "/playground": { label: "Playground", crumbs: [{ label: "Home", href: "/dashboard" }, { label: "Playground" }] },
  "/collections": { label: "Collections", crumbs: [{ label: "Home", href: "/dashboard" }, { label: "Collections" }] },
  "/postcodes": { label: "Postcodes/ZIP", crumbs: [{ label: "Home", href: "/dashboard" }, { label: "Postcodes/ZIP" }] },
  "/usage": { label: "Usage", crumbs: [{ label: "Home", href: "/dashboard" }, { label: "Usage" }] },
  "/documentation-list": { label: "Documentation", crumbs: [{ label: "Home", href: "/dashboard" }, { label: "Documentation" }] },
  "/account": { label: "Account & Billing", crumbs: [{ label: "Home", href: "/dashboard" }, { label: "Plan & Payment" }] },
  "/profile": { label: "Profile", crumbs: [{ label: "Home", href: "/dashboard" }, { label: "Profile" }] },
  "/error-logs": { label: "Error Logs", crumbs: [{ label: "Home", href: "/dashboard" }, { label: "Error Logs" }] },
};

export const TopBar = () => {
  const location = useLocation();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  // Find breadcrumb info — exact match, else the longest path-prefix match so
  // nested routes (e.g. /collections/:id/records) inherit their section title
  // instead of falling back to "Dashboard".
  const matchKey = Object.keys(BREADCRUMB_MAP)
    .filter(
      (k) => location.pathname === k || location.pathname.startsWith(k + "/")
    )
    .sort((a, b) => b.length - a.length)[0];
  const breadcrumbInfo = (matchKey && BREADCRUMB_MAP[matchKey]) || {
    label: "Dashboard",
    crumbs: [{ label: "Home", href: "/dashboard" }, { label: "Dashboard" }],
  };

  // Get user data from localStorage
  const AUTH = JSON.parse(window.localStorage.getItem("AUTH") || '{}');
  const userInitials = AUTH.name
    ? AUTH.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "AL";

  const handleSidebarToggle = () => {
    document.body.classList.toggle("mca-sidebar-open");
  };

  return (
    <header className="mca-topbar">
      <div className="mca-topbar__left">
        <button
          className="mca-iconbtn mca-hamburger"
          data-sidebar-toggle
          onClick={handleSidebarToggle}
          aria-label="Open menu"
        >
          {svg(I.hamburger, 2)}
        </button>
        <div>
          {/* Breadcrumb only — the big page title lives in each screen's own
              body header (<h1>), so a second copy here was duplicated. */}
          <nav className="mca-crumbs" aria-label="Breadcrumb">
            {breadcrumbInfo.crumbs.map((crumb, idx) => (
              <div key={idx}>
                {idx < breadcrumbInfo.crumbs.length - 1 ? (
                  <>
                    <Link to={crumb.href}>{crumb.label}</Link>
                    {svg(I.chevR, 2)}
                  </>
                ) : (
                  <span className="current">{crumb.label}</span>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>

      <div className="mca-topbar__right">
        {/* Theme toggle */}
        <ThemeToggle />

        {/* Notifications */}
        <div
          className={`mca-topbar__notif${notifOpen ? " is-open" : ""}`}
          data-menu
        >
          <button
            className="mca-iconbtn"
            aria-label="Notifications"
            aria-haspopup="menu"
            aria-expanded={notifOpen}
            onClick={() => setNotifOpen((o) => !o)}
          >
            {svg(I.bell)}
          </button>
          {notifOpen && (
            <div
              className="mca-menu mca-menu--right"
              role="menu"
              style={{ minWidth: "260px" }}
            >
              <div style={{ padding: "8px 12px 4px", fontFamily: "var(--mca-font-heading)", fontWeight: 600, fontSize: "14px" }}>
                Notifications
              </div>
              <div className="mca-menu__sep"></div>
              <div style={{ padding: "22px 12px", textAlign: "center", color: "var(--mca-muted)", fontSize: "13px" }}>
                You're all caught up — no new notifications.
              </div>
            </div>
          )}
        </div>

        {/* Profile menu */}
        <div
          className={`mca-topbar__profile${profileMenuOpen ? " is-open" : ""}`}
          data-menu
        >
          <button
            className="mca-profile-trigger"
            data-menu-trigger
            aria-haspopup="menu"
            aria-label="Account menu"
            onClick={() => setProfileMenuOpen(!profileMenuOpen)}
          >
            <span className="mca-avatar">{userInitials}</span>
            {svg(I.chev, 2)}
          </button>
          {profileMenuOpen && (
            <div className="mca-menu" role="menu">
              <div style={{ padding: "8px 10px 10px" }}>
                <div style={{ fontFamily: "var(--mca-font-heading)", fontWeight: "600", fontSize: "14px" }}>
                  {AUTH.name || "Alex Lawson"}
                </div>
                <div style={{ fontSize: "12px", color: "var(--mca-muted)" }}>Professional plan</div>
              </div>
              <div className="mca-menu__sep"></div>
              <Link to="/profile" className="mca-menu__item" role="menuitem" onClick={() => setProfileMenuOpen(false)}>
                {svg(I.user)}
                My Profile
              </Link>
              <Link to="/account" className="mca-menu__item" role="menuitem" onClick={() => setProfileMenuOpen(false)}>
                {svg(I.card)}
                Billing
              </Link>
              <div className="mca-menu__sep"></div>
              <button
                className="mca-menu__item mca-menu__item--danger"
                role="menuitem"
                onClick={() => {
                  window.localStorage.clear();
                  window.location.href = "/sign-in";
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 4h3a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-3" />
                  <path d="M10 17 5 12l5-5" />
                  <path d="M5 12h11" />
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
